import { ref } from 'vue'
import axiosInstance from '@/axios'

/**
 * useEnableInlineImages -- shared read of the SuperAdmin
 * ``enable_inline_images`` platform-settings toggle.
 *
 * The TipTap RichTextEditor wrapper exposes an ``imagesEnabled`` prop that
 * defaults FALSE everywhere. The two live editor sites (store branding
 * welcome message + category text) read THIS one boolean to decide whether
 * to render the inline-image button. This composable is the single shared
 * read so both sites use the same value AND only one GET fires per page
 * load (module-level cache + in-flight dedupe, mirroring
 * useHideUnpreviewableColors's pattern).
 *
 * Fail-SAFE default: ``enableInlineImages`` reads FALSE while the fetch is
 * in flight AND if the fetch errors or returns a non-boolean payload. This
 * matches the backend's unset default (also FALSE). The safe direction is
 * images OFF: enabling images WIDENS the sanitizer's accepted surface, so
 * a transient read failure must never surprise-ENABLE image authoring --
 * uncertainty keeps images off, never on.
 *
 * Public surface:
 *
 *   const { enableInlineImages, loading, error } = useEnableInlineImages()
 *
 *   enableInlineImages: Ref<boolean>          // TRUE = show image button
 *   loading:            Ref<boolean>          // request in flight
 *   error:              Ref<Error|null>       // any fetch error (fails safe)
 */
// Use the /api-prefixed form to match the peer call sites for this SAME
// endpoint (PlatformSettings.vue) and the other platform-settings reads.
const ENDPOINT = '/api/platform-settings/enable-inline-images'

// Module-level shared state: one fetch per page load, shared across every
// caller. ``_value`` holds the last resolved boolean (or the FALSE
// fail-safe default until/unless a real fetch resolves it).
const _value = ref(false)
const _loading = ref(false)
const _error = ref(null)
let _inflight = null
let _resolved = false

function _fetchOnce() {
    if (_resolved) {
        return Promise.resolve(_value.value)
    }
    if (_inflight) {
        return _inflight
    }
    _loading.value = true
    _error.value = null
    _inflight = axiosInstance
        .get(ENDPOINT)
        .then((resp) => {
            const raw = resp && resp.data ? resp.data.value : null
            // Only a real boolean overrides the fail-safe default. Any other
            // shape (missing key, string, number, null) leaves it FALSE.
            if (typeof raw === 'boolean') {
                _value.value = raw
            } else {
                _value.value = false
            }
            _resolved = true
            return _value.value
        })
        .catch((err) => {
            // Fail SAFE: keep images OFF on a read error. Do NOT mark
            // resolved so a later mount can retry the fetch.
            _error.value = err
            _value.value = false
            return _value.value
        })
        .finally(() => {
            _loading.value = false
            _inflight = null
        })
    return _inflight
}

/**
 * Test-only: reset the module-level shared state so specs re-isolate per
 * test without a prior fetch bleeding across cases.
 */
export function _resetEnableInlineImagesCache() {
    _value.value = false
    _loading.value = false
    _error.value = null
    _inflight = null
    _resolved = false
}

export function useEnableInlineImages() {
    // Kick the shared fetch on first use; subsequent callers reuse it.
    _fetchOnce()
    return {
        enableInlineImages: _value,
        loading: _loading,
        error: _error,
    }
}

export default useEnableInlineImages
